import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./WeeklyReportBoard.module.css";
import { LoadBoard, SaveBoard } from "../utils/boardApi";
import { useAuth } from "../context/AuthContext";
import XLSX from 'xlsx-js-style';

// --- Global Helpers ---

const positionPriority: Record<string, number> = {
  "이사": 1,
  "상무": 2,
  "팀장": 3,
  "부장": 4,
  "차장": 5,
  "과장": 6,
  "대리": 7,
  "사원": 8,
};

const getPositionPriority = (pos: string) => positionPriority[pos] || 99;

const getWeekNumber = (date: Date): number => {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const firstMonday = new Date(firstDayOfYear);
  firstMonday.setDate(
    firstDayOfYear.getDate() + ((2 - firstDayOfYear.getDay() + 7) % 7)
  );
  const diff: number = date.getTime() - firstMonday.getTime();
  return Math.ceil(diff / (7 * 24 * 60 * 60 * 1000)) + 1;
};

const getMonthWeekLabel = (weekNumber: number): string => {
  const firstDayOfYear = new Date(new Date().getFullYear(), 0, 1);
  const targetDate = new Date(firstDayOfYear);
  targetDate.setDate(firstDayOfYear.getDate() + (weekNumber - 1) * 7);
  const month: number = targetDate.getMonth() + 1;
  const weekOfMonth: number = Math.ceil(targetDate.getDate() / 7);
  return `${month}월 ${weekOfMonth}주차`;
};

const transData = (loadData: Board) => {
  const categories = loadData.category.split("^^").map((item) => item.trim());
  const weeklyPlan = loadData.currentWeekPlan.split("^^").map((item) => item.trim());
  const prevPlan = loadData.previousWeekPlan.split("^^").map((item) => item.trim());
  const prevResult = loadData.performance.split("^^").map((item) => item.trim());
  const completion = loadData.completionDate.split("^^").map((item) => item.trim());
  const progress = loadData.achievementRate.split("^^").map((item) => item.trim());
  const allprogress = loadData.totalRate.split("^^").map((item) => item.trim());
  const pm = loadData?.pm ? loadData.pm.split("^^").map((item) => item.trim()) : [];

  return categories.map((_, index) => ({
    category: categories[index] || "",
    weeklyPlan: weeklyPlan[index] || "",
    prevPlan: prevPlan[index] || "",
    prevResult: prevResult[index] || "",
    completion: completion[index] || "",
    progress: progress[index] || "",
    allprogress: allprogress[index] || "",
    pm: pm[index] || "",
  }));
};

const getCommonStyles = () => {
  const getBorderStyle = (color = "222222") => ({
    top: { style: "thin" as const, color: { rgb: color } },
    bottom: { style: "thin" as const, color: { rgb: color } },
    left: { style: "thin" as const, color: { rgb: color } },
    right: { style: "thin" as const, color: { rgb: color } }
  });

  const createStyle = (bgColor = "ffffff", isBold = true, borderColor = "222222", align: "center" | "left" | "right" = "center") => ({
    fill: { fgColor: { rgb: bgColor } },
    font: { bold: isBold, name: "맑은 고딕", sz: 10 },
    alignment: { horizontal: align, vertical: "center" as const, wrapText: true },
    border: getBorderStyle(borderColor)
  });

  return {
    B: createStyle("dce6f1", true),
    Y: createStyle("ffffcc", true),
    R: createStyle("ffcccc", true),
    W: createStyle("ffffff", false, "222222", "left"),
    A: createStyle("ffffff", false, "222222", "center"),
    memoHeader1: createStyle("dce6f1", true, "bfbfbf"),
    memoHeader2: createStyle("ffcccc", true, "bfbfbf"),
    memoHeader3: createStyle("ffffcc", true, "bfbfbf"),
    memoData: createStyle("ffffff", false, "bfbfbf", "left"),
    getBorderStyle,
    createStyle
  };
};

// --- Interfaces ---

interface User {
  id: string;
  userName: string;
}

interface Board {
  id: string;
  team: { id: string; name: string };
  title: string;
  category: string;
  currentWeekPlan: string;
  previousWeekPlan: string;
  performance: string;
  completionDate: string;
  achievementRate: string;
  totalRate: string;
  report: string;
  issue: string;
  memo: string;
  user: User;
  pm: string;
  version: number;
}

interface ContextMenuState {
  mouseX: number;
  mouseY: number;
  rowIndex: number;
}

interface MemoTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  readOnly?: boolean;
}

const MemoTextarea = React.memo(function MemoTextarea({ value, onChange, readOnly, ...props }: MemoTextareaProps) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const adjustHeight = () => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = `${ref.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [value])

  return (
    <textarea
      ref={ref}
      value={value}
      readOnly={readOnly}
      onChange={(e) => {
        if (readOnly) return;
        onChange(e);
        adjustHeight();
      }}
      {...props}
    />
  );
});

// --- Components ---

const WeeklyReportBoard: React.FC = () => {
  const [currentWeek, setCurrentWeek] = useState<number | null>(null);
  const [recentWeeks, setRecentWeeks] = useState<(number | string)[]>([]);
  const [nextWeek, setNextWeek] = useState<number | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());

  const [isBoardLoaded, setIsBoardLoaded] = useState(false);
  const [data, setData] = useState<Board[]>([]);
  const { isAuth, userId, userRole, userTeamId, userTeamName, userName, userPosition } = useAuth();

  const [isPending, setIsPending] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // --- Own Report State ---
  const [reportData, setReportData] = useState([{ category: "", weeklyPlan: "", prevPlan: "", prevResult: "", completion: "202 . . ", progress: "0", allprogress: "0", pm: "" }]);
  const [infoContent, setInfoContent] = useState("");
  const [issueContent, setIssueContent] = useState("");
  const [memoContent, setMemoContent] = useState("");
  const [currentBoardVersion, setCurrentBoardVersion] = useState<number | undefined>(undefined);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  const fetchData = async () => {
    if (!userId || !userRole) return;
    setIsPending(true);
    setFetchError(null);
    try {
      const result = await LoadBoard(userId, userRole);
      setData(result);
      setIsBoardLoaded(true);
    } catch (err: any) {
      setFetchError(err.message || "데이터를 불러오는 중 오류가 발생했습니다.");
    } finally {
      setIsPending(false);
    }
  };

  useEffect(() => {
    const dateNow = new Date();
    const weekNow = getWeekNumber(dateNow);
    const yearNow = dateNow.getFullYear();

    if (yearNow !== currentYear) {
      setCurrentYear(yearNow);
      setRecentWeeks([48, 49, 50, 51, 52, 1]);
    } else {
      setRecentWeeks((prev) => {
        const last6 = [];
        for (let i = 5; i >= 0; i--) {
          let w = weekNow - i;
          if (w <= 0) w += 52;
          last6.push(w);
        }
        return Array.from(new Set([...last6, ...prev]));
      });
    }

    setSelectedWeek(weekNow);
  }, [isAuth, userId, userRole, currentYear]);

  useEffect(() => {
    if (isAuth && userId) {
      fetchData();
    }
  }, [isAuth, userId, userRole]);

  useEffect(() => {
    if (selectedWeek !== null) {
      setCurrentWeek(selectedWeek);
      setNextWeek(selectedWeek < 52 ? selectedWeek + 1 : 1);
    }
  }, [selectedWeek]);

  // --- Current Week Processing ---
  const currentWeekLabel = currentWeek !== null ? getMonthWeekLabel(currentWeek) : "";
  
  // Create the list of boards to display, sorted by placing own board first, then by position and name
  const displayBoards = [...data]
    .filter(b => b.title === currentWeekLabel)
    .sort((a, b) => {
      const isAMine = a.user.id === userId;
      const isBMine = b.user.id === userId;
      if (isAMine && !isBMine) return -1;
      if (!isAMine && isBMine) return 1;

      const pA = getPositionPriority((a.user as any).position || "");
      const pB = getPositionPriority((b.user as any).position || "");
      if (pA !== pB) return pA - pB;
      return a.user.userName.localeCompare(b.user.userName);
    });

  // Identify my board in the sorted list (if any)
  const ownBoard = displayBoards.find(b => b.user.id === userId);

  useEffect(() => {
    if (!isBoardLoaded || !currentWeek) return;
    
    if (ownBoard) {
      setReportData(transData(ownBoard));
      setInfoContent(ownBoard.report);
      setIssueContent(ownBoard.issue);
      setMemoContent(ownBoard.memo);
      setCurrentBoardVersion(ownBoard.version);
    } else {
      setReportData([{ category: "", weeklyPlan: "", prevPlan: "", prevResult: "", completion: "202 . . ", progress: "0", allprogress: "0", pm: "" }]);
      setInfoContent("");
      setIssueContent("");
      setMemoContent("");
      setCurrentBoardVersion(undefined);
    }

    // 파트장 또는 팀원인 경우 팀원들의 정보보고/이슈/메모를 줄바꿈으로 합쳐서 표시 (본인 것 제외)
    if (userRole === '파트장' || userRole === '팀원' || userRole === '관리자') {
      const otherBoards = displayBoards.filter(b => b.user.id !== userId);
      const combinedReport = [ownBoard?.report || "", ...otherBoards.map(b => b.report)].filter(t => !!t).join("\n\n");
      const combinedIssue = [ownBoard?.issue || "", ...otherBoards.map(b => b.issue)].filter(t => !!t).join("\n\n");
      const combinedMemo = [ownBoard?.memo || "", ...otherBoards.map(b => b.memo)].filter(t => !!t).join("\n\n");
      
      setInfoContent(combinedReport);
      setIssueContent(combinedIssue);
      setMemoContent(combinedMemo);
    }
  }, [currentWeek, isBoardLoaded, ownBoard, userId, userRole, displayBoards.length]);

  // Handle the case where I haven't submitted but still need to be in the sorted list (for Part Leader view)
  const finalDisplayBoards = [...displayBoards];
  if (!ownBoard && isAuth && userId) {
    const myVirtualBoard = {
      id: 'mine-new',
      user: { id: userId, userName: userName, position: userPosition },
      team: { id: userTeamId, name: "" },
      title: currentWeekLabel,
    } as any;
    finalDisplayBoards.push(myVirtualBoard);
    finalDisplayBoards.sort((a, b) => {
      const isAMine = a.user.id === userId;
      const isBMine = b.user.id === userId;
      if (isAMine && !isBMine) return -1;
      if (!isAMine && isBMine) return 1;

      const pA = getPositionPriority((a.user as any).position || "");
      const pB = getPositionPriority((b.user as any).position || "");
      if (pA !== pB) return pA - pB;
      return a.user.userName.localeCompare(b.user.userName);
    });
  }

  // --- Handlers ---
  const handleMainChange = (index: number, field: string, value: string) => {
    const newData = [...reportData];
    newData[index] = { ...newData[index], [field]: value };
    setReportData(newData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>, type: string) => {
    if (type === "info") setInfoContent(e.target.value);
    if (type === "issue") setIssueContent(e.target.value);
    if (type === "memo") setMemoContent(e.target.value);
  };

  const handleAddRow = () => {
    setReportData([...reportData, { category: "", weeklyPlan: "", prevPlan: "", prevResult: "", completion: "202 . . ", progress: "0", allprogress: "0", pm: "" }]);
  };

  const handleAddRow2 = () => {
    if (!contextMenu) return;
    const index = contextMenu.rowIndex;
    const newData = [...reportData];
    newData.splice(index + 1, 0, { category: "", weeklyPlan: "", prevPlan: "", prevResult: "", completion: "202 . . ", progress: "0", allprogress: "0", pm: "" });
    setReportData(newData);
    setContextMenu(null);
  };

  const handleDeleteRow = () => {
    if (!contextMenu) return;
    const index = contextMenu.rowIndex;
    if (reportData.length === 1) return;
    const newData = [...reportData];
    newData.splice(index, 1);
    setReportData(newData);
    setContextMenu(null);
  };

  const OnSave = async () => {
    if (!window.confirm("저장하시겠습니까?")) return;
    const board = {
      title: currentWeekLabel,
      category: reportData.map((obj) => obj.category).join("^^"),
      currentWeekPlan: reportData.map((obj) => obj.weeklyPlan).join("^^"),
      previousWeekPlan: reportData.map((obj) => obj.prevPlan).join("^^"),
      performance: reportData.map((obj) => obj.prevResult).join("^^"),
      completionDate: reportData.map((obj) => obj.completion).join("^^"),
      achievementRate: reportData.map((obj) => obj.progress).join("^^"),
      totalRate: reportData.map((obj) => obj.allprogress).join("^^"),
      pm: reportData.map((obj) => obj.pm).join("^^"),
      report: infoContent,
      issue: issueContent,
      memo: memoContent,
      version: currentBoardVersion,
    };
    try {
      const resp = await SaveBoard(board, userId);
      alert(resp);
      fetchData();
    } catch (err: any) {
      if (err.response?.status === 409) alert(err.response.data.message + " 다시 시도해 주세요.");
      else alert("저장 중 오류가 발생했습니다.");
    }
  };

  const onCopyAndPaste = async () => {
    if (currentWeek === null) return;
    if (!window.confirm("이전 주차 내용으로 업데이트 하시겠습니까?")) return;
    const prevTitle = getMonthWeekLabel(currentWeek - 1);
    const prevBoard = data.find(item => item.title === prevTitle && item.user.id === userId);
    if (!prevBoard) { alert("해당 주차의 데이터가 없습니다"); return; }
    setReportData(transData(prevBoard));
    setInfoContent(prevBoard.report);
    setIssueContent(prevBoard.issue);
    setMemoContent(prevBoard.memo);
  };

  const exportToExcel = () => {
    try {
      const weekLabel = currentWeekLabel;
      if (!weekLabel) return;
      
      if (!displayBoards || displayBoards.length === 0) { alert("해당 주차에 데이터가 없습니다."); return; }

      let totalBodyRows: any[] = [];
      let totalReportArr: string[] = [];
      let totalIssueArr: string[] = [];
      let totalMemoArr: string[] = [];

      const teamGroups: Record<string, { name: string, reports: string[], issues: string[], memos: string[] }> = {};

      displayBoards.forEach((board: Board) => {
        const teamId = board.team?.id || "unknown";
        const teamName = board.team?.name || userTeamName || "소속 파트";
        
        if (!teamGroups[teamId]) {
          teamGroups[teamId] = { name: teamName, reports: [], issues: [], memos: [] };
        }
        
        if (board.report) teamGroups[teamId].reports.push(board.report);
        if (board.issue) teamGroups[teamId].issues.push(board.issue);
        if (board.memo) teamGroups[teamId].memos.push(board.memo);

        const teamRows = transData(board);
        teamRows.forEach((row) => {
          totalBodyRows.push([
            row.category, 
            row.weeklyPlan, 
            row.prevPlan, 
            row.prevResult, 
            row.completion, 
            row.progress + "%", 
            row.allprogress + "%", 
            row.pm
          ]);
        });
      });

      Object.values(teamGroups).forEach(group => {
        if (group.reports.length > 0) totalReportArr.push(group.reports.join("\n\n"));
        if (group.issues.length > 0) totalIssueArr.push(group.issues.join("\n\n"));
        if (group.memos.length > 0) totalMemoArr.push(group.memos.join("\n\n"));
      });

      const ws = XLSX.utils.aoa_to_sheet([
        [`${currentYear}년 ${weekLabel} 주간업무보고 (${userTeamName || '소속 파트'})`],
        ["구분", nextWeek !== null ? getMonthWeekLabel(nextWeek) : "", weekLabel, "", "완료예정일", "달성율", "", "PM"],
        ["", "계획업무", "계획업무", "수행실적", "(yyyy.mm.dd)", "금주", "전체", ""],
        ...totalBodyRows
      ]);
      
      const styles_xlsx = getCommonStyles();

      // 헤더 스타일 및 병합 (8컬럼 기준)
      for (let R = 0; R <= 2; R++) {
        for (let C = 0; C <= 7; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[addr]) ws[addr] = { t: "s", v: "" };
          if (R === 0) ws[addr].s = styles_xlsx.createStyle("ffffff", true, "222222", "center");
          else {
            if (C === 1) ws[addr].s = styles_xlsx.Y;
            else if (C === 2 || C === 3) ws[addr].s = styles_xlsx.R;
            else ws[addr].s = styles_xlsx.B;
          }
        }
      }

      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }, // 타이틀
        { s: { r: 1, c: 0 }, e: { r: 2, c: 0 } }, // 구분
        { s: { r: 1, c: 1 }, e: { r: 2, c: 1 } }, // 차주계획
        { s: { r: 1, c: 2 }, e: { r: 1, c: 3 } }, // 금주계획/실적 그룹
        { s: { r: 1, c: 4 }, e: { r: 2, c: 4 } }, // 완료예정일
        { s: { r: 1, c: 5 }, e: { r: 1, c: 6 } }, // 달성율 그룹
        { s: { r: 1, c: 7 }, e: { r: 2, c: 7 } }, // PM
      ];

      // 데이터 영역 스타일
      totalBodyRows.forEach((_, rIdx) => {
        const rAddr = rIdx + 3;
        for (let C = 0; C <= 7; C++) {
          const cellRef = XLSX.utils.encode_cell({ r: rAddr, c: C });
          if (!ws[cellRef]) ws[cellRef] = { t: "s", v: "" };
          ws[cellRef].s = (C === 0 || C === 4 || C === 5 || C === 6 || C === 7) ? styles_xlsx.A : styles_xlsx.W;
        }
      });

      const startRow = totalBodyRows.length + 4;
      XLSX.utils.sheet_add_aoa(ws, [["정보보고", "", "이슈", "", "메모", "", "", ""]], { origin: `A${startRow + 1}` });
      XLSX.utils.sheet_add_aoa(ws, [[totalReportArr.join("\n\n"), "", totalIssueArr.join("\n\n"), "", totalMemoArr.join("\n\n"), "", "", ""]], { origin: `A${startRow + 2}` });

      for (let R = startRow; R <= startRow + 1; R++) {
        for (let C = 0; C <= 7; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[addr]) ws[addr] = { t: "s", v: "" };
          if (R === startRow) {
            if (C < 2) ws[addr].s = styles_xlsx.memoHeader1;
            else if (C < 4) ws[addr].s = styles_xlsx.memoHeader2;
            else ws[addr].s = styles_xlsx.memoHeader3;
          } else {
            ws[addr].s = styles_xlsx.memoData;
          }
        }
      }

      ws['!merges'].push(
        { s: { r: startRow, c: 0 }, e: { r: startRow, c: 1 } },
        { s: { r: startRow, c: 2 }, e: { r: startRow, c: 3 } },
        { s: { r: startRow, c: 4 }, e: { r: startRow, c: 7 } },
        { s: { r: startRow + 1, c: 0 }, e: { r: startRow + 1, c: 1 } },
        { s: { r: startRow + 1, c: 2 }, e: { r: startRow + 1, c: 3 } },
        { s: { r: startRow + 1, c: 4 }, e: { r: startRow + 1, c: 7 } }
      );

      ws['!rows'] = []; 
      ws['!rows'][startRow + 1] = { hpt: 200 };
      ws['!cols'] = [ { wch: 30 }, { wch: 45 }, { wch: 45 }, { wch: 45 }, { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 15 } ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "주간보고");
      XLSX.writeFile(wb, `${userTeamName || '파트'}_주간업무보고_${weekLabel}.xlsx`);
    } catch (err) {
      console.error(err);
      alert("엑셀 생성 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.section1}>
        <div className={styles.topBar}>
          <div className={styles.dropdownContainer}>
            <select className={styles.dropdown} value={selectedWeek || ""} onChange={(e) => setSelectedWeek(Number(e.target.value))}>
              {recentWeeks.map((week, index) => ( <option key={index} value={week}>{`${week}주 (${getMonthWeekLabel(Number(week))})`}</option> ))}
            </select>
          </div>
          <h3 className={styles.title}>{currentWeekLabel} 업무보고</h3>
          <div style={{ display: "flex" }}>
            <button className={styles.exportAllButton} onClick={exportToExcel}>엑셀 내보내기</button>
            <button className={styles.copyButton} onClick={onCopyAndPaste}>전 주차 Copy</button>
            <button className={styles.rowAddButton} onClick={handleAddRow}>Row Add</button>
            <button className={styles.saveButton} onClick={OnSave}>Save</button>
          </div>
        </div>
        {isPending && <p>⏳ 데이터를 불러오는 중...</p>}
        {fetchError && <p style={{ color: "red" }}>⚠️ {fetchError}</p>}
        
        <div className={styles.reportTableContainer}>
          <table className={styles.reportTable} style={{ width: "100%" }}>
            <thead>
              <tr>
                <th rowSpan={2} className={styles.header}>구분</th>
                <th colSpan={1} className={styles.weeklyReport}>{nextWeek !== null ? getMonthWeekLabel(nextWeek) : ""}</th>
                <th colSpan={2} className={styles.prevWeek}>{currentWeek !== null ? getMonthWeekLabel(currentWeek) : ""}</th>
                <th rowSpan={1} className={styles.Completion}>완료예정일</th>
                <th colSpan={2} className={styles.Progress}>달성율</th>
                <th rowSpan={2} className={styles.Progress}>PM</th>
              </tr>
              <tr>
                <th className={styles.weeklyReport}>계획업무</th>
                <th className={styles.prevWeek}>계획업무</th>
                <th className={styles.prevWeek}>수행실적</th>
                <th className={styles.Completion}>(yyyy.mm.dd)</th>
                <th className={styles.Progress}>금주</th>
                <th className={styles.Progress}>전체</th>
              </tr>
            </thead>
            <tbody>
              {finalDisplayBoards.map((board, bIdx) => {
                const isMine = board.user.id === userId;
                const boardRows = isMine ? reportData : transData(board);
                return (
                  <React.Fragment key={board.id}>
                    {bIdx > 0 && <tr style={{ height: '8px', backgroundColor: '#f0f0f0' }}><td colSpan={8}></td></tr>}
                    {boardRows.map((row, index) => (
                      <tr key={`${board.id}-${index}`} 
                          style={{ backgroundColor: !isMine ? '#f5f5f5' : 'transparent' }}
                          onContextMenu={(e) => { 
                            if (!isMine) return;
                            e.preventDefault(); 
                            setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, rowIndex: index }); 
                          }}>
                        {Object.keys(row).map((field, colIndex) => (
                          <td key={field} className={styles.mainscrollableCell}>
                            {["progress", "allprogress", "completion"].includes(field) ? (
                              <div style={{ display: "flex", justifyContent: "center" }}>
                                <input type="text" 
                                       className={styles.inputField} 
                                       value={row[field as keyof typeof row]} 
                                       readOnly={!isMine}
                                       onChange={(e) => isMine && handleMainChange(index, field, e.target.value)} />
                                {["progress", "allprogress"].includes(field) && <span>%</span>}
                              </div>
                            ) : (
                              <MemoTextarea style={{ overflowY: "auto", maxHeight: "200px", resize: "none" }} 
                                            className={colIndex === 0 ? styles.FirstTextArea : colIndex === 7 ? styles.PMArea : styles.MaintextArea} 
                                            value={row[field as keyof typeof row]} 
                                            readOnly={!isMine}
                                            onChange={(e) => isMine && handleMainChange(index, field, e.target.value)} />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {contextMenu && (
            <div style={{ position: "fixed", top: contextMenu.mouseY, left: contextMenu.mouseX, background: "white", border: "1px solid gray", borderRadius: "0.4rem", padding: "0.5rem", boxShadow: "2px 2px 5px rgba(0,0,0,0.2)", cursor: "pointer", zIndex: 1000, display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              <div onClick={handleAddRow2} style={{ padding: "4px 8px" }}>행 추가 (아래로)</div>
              <div style={{ borderBottom: "1px solid #eee" }}></div>
              <div onClick={handleDeleteRow} style={{ padding: "4px 8px", color: "red" }}>행 삭제</div>
            </div>
          )}
        </div>
      </div>

      {/* Info/Issue/Memo Section */}
      <div className={styles.section2}>
        <table className={styles.infoTable}>
          <tbody>
            <tr>
              <th className={styles.infoHeader}>정보보고</th>
              <td className={styles.scrollableCell}>
                <textarea className={styles.textArea} value={infoContent} onChange={(e) => handleInputChange(e, "info")} />
              </td>
              <th className={styles.issueHeader}>이슈</th>
              <td className={styles.scrollableCell}>
                <textarea className={styles.textArea} value={issueContent} onChange={(e) => handleInputChange(e, "issue")} />
              </td>
              <th className={styles.memoHeader}>메모</th>
              <td className={styles.scrollableCell}>
                <textarea className={styles.textArea} value={memoContent} onChange={(e) => handleInputChange(e, "memo")} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WeeklyReportBoard;
